/**
 * Perfil completo de deputado(a) federal PE — Brasil Transparente.
 *
 * Rota: /brasil-transparente/deputado/{id}
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import {
  ArrowLeft, AlertCircle, Share2, ExternalLink, Mail, Phone, Building,
  ChevronRight, Info,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const brl = (v) =>
  typeof v === 'number'
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';
const num = (v) => (typeof v === 'number' ? v.toLocaleString('pt-BR') : '—');

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function BrasilTransparenteDeputado() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setErr(false);
      try {
        const r = await axios.get(
          `${API}/public/brasil-transparente/PE/deputado/${id}`,
          { timeout: 45000 },
        );
        if (!cancel) setData(r.data);
      } catch { if (!cancel) setErr(true); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [id]);

  const base = data?.base;
  const det = data?.detalhe;
  const desp = data?.despesas;
  const shareUrl = `${BACKEND_URL}/api/public/brasil-transparente/PE/share/deputado/${id}.png`;

  return (
    <div className="min-h-screen bg-neutral-950 text-white pb-24" data-testid="bt-deputado-page">
      <header className="border-b border-neutral-800 bg-gradient-to-br from-emerald-900/40 via-neutral-950 to-neutral-950 px-4 py-6">
        <div className="max-w-5xl mx-auto">
          <Link
            to="/brasil-transparente"
            className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-emerald-400"
            data-testid="back-to-bt"
          >
            <ArrowLeft size={14} /> Brasil Transparente
          </Link>

          {loading && <div className="h-24 mt-4 animate-pulse bg-neutral-900 rounded" />}
          {!loading && err && <ErrorBox />}

          {!loading && base && (
            <div className="mt-4 flex items-start gap-4">
              <img
                src={base.foto || 'https://via.placeholder.com/120x160?text=%20'}
                alt={base.nome}
                className="w-20 h-24 sm:w-24 sm:h-28 object-cover rounded bg-neutral-800 shrink-0"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-widest text-emerald-300/80 font-mono">
                  Deputado(a) Federal · PE
                </div>
                <h1 className="text-2xl sm:text-4xl font-black tracking-tight leading-tight mt-1">
                  {base.nome}
                </h1>
                <div className="text-sm text-neutral-300 mt-1">
                  <span className="text-emerald-400 font-semibold">{base.partido}</span>
                  {det?.escolaridade && <> · {det.escolaridade}</>}
                  {det?.municipio_nascimento && (
                    <> · Nasceu em {det.municipio_nascimento}/{det.uf_nascimento}</>
                  )}
                </div>
                {det?.nome_civil && det.nome_civil !== base.nome && (
                  <div className="text-[11px] text-neutral-500 italic mt-0.5">
                    Nome civil: {det.nome_civil}
                  </div>
                )}
                {(det?.situacao || det?.condicao_eleitoral || det?.sexo || det?.data_nascimento) && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {det.situacao && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/40"
                        data-testid="dep-situacao"
                      >
                        {det.situacao}
                      </span>
                    )}
                    {det.condicao_eleitoral && (
                      <span
                        className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-neutral-800 text-neutral-300 border border-neutral-700"
                        data-testid="dep-condicao"
                      >
                        {det.condicao_eleitoral}
                      </span>
                    )}
                    {det.sexo && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-neutral-800 text-neutral-400 border border-neutral-700">
                        {det.sexo === 'M' ? 'Masculino' : det.sexo === 'F' ? 'Feminino' : det.sexo}
                      </span>
                    )}
                    {det.data_nascimento && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-neutral-800 text-neutral-400 border border-neutral-700">
                        Nasc.: {det.data_nascimento.split('-').reverse().join('/')}
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {base.email && (
                    <a
                      href={`mailto:${base.email}`}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300 hover:border-emerald-500"
                      data-testid="dep-email"
                    ><Mail size={12} /> Email</a>
                  )}
                  {det?.gabinete?.telefone && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300">
                      <Phone size={12} /> {det.gabinete.telefone}
                    </span>
                  )}
                  {det?.gabinete?.sala && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-neutral-900 border border-neutral-800 text-[11px] text-neutral-300">
                      <Building size={12} /> Sala {det.gabinete.sala}
                      {det.gabinete.andar && ` · ${det.gabinete.andar}º andar`}
                    </span>
                  )}
                  <a
                    href={shareUrl}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="share-dep-png"
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-[11px] hover:bg-emerald-500/20"
                  ><Share2 size={12} /> Card WhatsApp</a>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {data?.aviso_emendas && (
          <Card className="bg-amber-900/20 border-amber-800/50 p-3 text-xs text-amber-200 flex gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>{data.aviso_emendas}</span>
          </Card>
        )}

        {desp && desp.ok && (
          <>
            {/* Totais gabinete */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <Metric
                label={`Gastos de gabinete ${desp.ano}`}
                value={brl(desp.total)}
                testid="dep-total"
              />
              <Metric
                label="Despesas"
                value={num(desp.count)}
                hint="registradas na CEAP"
                testid="dep-count"
              />
              <Metric
                label="Categorias distintas"
                value={num((desp.por_categoria || []).length)}
                testid="dep-cats"
              />
            </div>

            <Section title="Por categoria (CEAP)" testid="section-cat">
              <div className="space-y-1.5">
                {(desp.por_categoria || []).map((c, i) => (
                  <Bar
                    key={c.categoria + i}
                    label={c.categoria}
                    value={c.total}
                    max={desp.por_categoria[0]?.total || 1}
                    hint={`${num(c.count)} despesas`}
                    testid={`cat-bar-${i}`}
                  />
                ))}
              </div>
            </Section>

            <Section title="Evolução mensal" testid="section-mes">
              <div className="flex items-end gap-1 h-32 sm:h-40 border border-neutral-800 rounded bg-neutral-900/60 p-3">
                {(desp.por_mes || []).map((m) => {
                  const maxMes = Math.max(1, ...(desp.por_mes || []).map((x) => x.total));
                  const pct = (m.total / maxMes) * 100;
                  return (
                    <div
                      key={m.mes}
                      className="flex-1 flex flex-col items-center gap-1 justify-end"
                      data-testid={`mes-${m.mes}`}
                    >
                      <div
                        className="w-full bg-emerald-500/70 rounded-sm min-h-[2px]"
                        style={{ height: `${pct}%` }}
                        title={brl(m.total)}
                      />
                      <div className="text-[9px] text-neutral-500">{MESES[m.mes - 1]}</div>
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Maiores fornecedores" testid="section-forn">
              <div className="space-y-2">
                {(desp.top_fornecedores || []).map((f, i) => {
                  const cnpjDigits = String(f.cnpj_cpf || '').replace(/\D/g, '');
                  const hasCnpj = cnpjDigits.length === 14;
                  const inner = (
                    <Card
                      className="bg-neutral-900 border-neutral-800 p-3 flex items-center gap-3 hover:border-emerald-500 transition"
                      data-testid={`forn-${i}`}
                    >
                      <div className="text-xs font-mono text-neutral-500 w-5 shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-white break-words">{f.nome}</div>
                        <div className="text-[10px] text-neutral-500 font-mono">
                          {f.cnpj_cpf || 'sem CNPJ'} · {num(f.count)} pagamentos
                        </div>
                      </div>
                      <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
                        {brl(f.total)}
                      </div>
                      {hasCnpj && <ChevronRight size={14} className="text-neutral-500 shrink-0" />}
                    </Card>
                  );
                  return hasCnpj ? (
                    <Link
                      key={(f.cnpj_cpf || f.nome) + i}
                      to={`/brasil-transparente/empresa/${cnpjDigits}`}
                      className="block"
                    >
                      {inner}
                    </Link>
                  ) : (
                    <div key={(f.cnpj_cpf || f.nome) + i}>{inner}</div>
                  );
                })}
              </div>
            </Section>

            <div className="text-[11px] text-neutral-500 flex items-center gap-1.5 pt-2">
              <ExternalLink size={12} /> Fonte: {desp.fonte}
            </div>
          </>
        )}

        {desp && !desp.ok && (
          <Card className="bg-neutral-900 border-neutral-800 p-4 text-sm text-neutral-400">
            Sem despesas retornadas pela Câmara para este ano ou serviço temporariamente indisponível.
          </Card>
        )}
      </main>
    </div>
  );
}

/* ── UI helpers ────────────────────────────── */
function Metric({ label, value, hint, testid }) {
  return (
    <Card className="bg-neutral-900 border-neutral-800 p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">{label}</div>
      <div className="text-lg sm:text-xl font-black text-white mt-1 leading-tight break-words">
        {value}
      </div>
      {hint && <div className="text-[10px] text-neutral-500 mt-1">{hint}</div>}
    </Card>
  );
}

function Section({ title, subtitle, children, testid }) {
  return (
    <section data-testid={testid}>
      <h2 className="text-sm font-semibold text-neutral-200">{title}</h2>
      {subtitle && <p className="text-[11px] text-neutral-500 mb-2 mt-0.5">{subtitle}</p>}
      <div className="mt-2">{children}</div>
    </section>
  );
}

function Bar({ label, value, max, hint, testid }) {
  const pct = max > 0 ? Math.max(3, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="relative rounded overflow-hidden bg-neutral-900 border border-neutral-800" data-testid={testid}>
      <div className="absolute inset-y-0 left-0 bg-emerald-500/20" style={{ width: `${pct}%` }} />
      <div className="relative flex items-center gap-2 px-3 py-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white truncate">{label}</div>
          {hint && <div className="text-[10px] text-neutral-500">{hint}</div>}
        </div>
        <div className="text-sm font-bold text-emerald-400 font-mono shrink-0">
          {brl(value)}
        </div>
      </div>
    </div>
  );
}

function ErrorBox() {
  return (
    <Card className="bg-red-950/30 border-red-900 p-4 text-sm text-red-200 flex gap-2 mt-4" data-testid="bt-dep-error">
      <AlertCircle size={16} className="shrink-0 mt-0.5" />
      <div>Não foi possível carregar o perfil deste(a) deputado(a).</div>
    </Card>
  );
}
